import { useState, useCallback, useEffect, useRef } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { SocketEvent, AdminSendAlertRequest, AdminSendAlertResponse } from '@swtorbit/shared-socket-events';
import { useGameContext } from '../contexts/GameContext';

// Predefined icon options
const ICON_OPTIONS = [
  { value: '📢', label: '📢 Alert' },
  { value: '⚠️', label: '⚠️ Warning' },
  { value: '🎮', label: '🎮 Game' },
  { value: '⭐', label: '⭐ Important' },
  { value: '🔥', label: '🔥 Urgent' },
  { value: '⏰', label: '⏰ Time' },
  { value: '🎯', label: '🎯 Target' },
  { value: '💡', label: '💡 Info' },
  { value: '✅', label: '✅ Success' },
  { value: '❌', label: '❌ Error' },
  { value: '🎉', label: '🎉 Celebration' },
  { value: '⚡', label: '⚡ Power' },
];

export function NotificationsPanel() {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();
  
  const [icon, setIcon] = useState('📢');
  const [heading, setHeading] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState(5);
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Track pending response handler for cleanup
  const pendingHandlerRef = useRef<((response: AdminSendAlertResponse) => void) | null>(null);
  
  // Cleanup pending socket.once listener on unmount
  useEffect(() => {
    return () => {
      if (pendingHandlerRef.current && socket) {
        socket.off(SocketEvent.ADMIN_SEND_ALERT, pendingHandlerRef.current);
        pendingHandlerRef.current = null;
      }
    };
  }, [socket]);

  const handleSend = useCallback(() => {
    if (!socket || !selectedGame) return;
    
    if (!heading.trim()) {
      setStatusMessage({ type: 'error', text: 'Heading is required' });
      return;
    }

    if (!description.trim()) {
      setStatusMessage({ type: 'error', text: 'Description is required' });
      return;
    }

    setIsSending(true);
    setStatusMessage(null);

    const request: AdminSendAlertRequest = {
      roomCode: selectedGame.roomCode,
      icon,
      heading: heading.trim(),
      description: description.trim(),
      durationSeconds: duration,
    };

    // Listen for response
    const handleResponse = (response: AdminSendAlertResponse) => {
      pendingHandlerRef.current = null; // Clear ref since handler executed
      setIsSending(false);
      if (response.success) {
        setStatusMessage({ type: 'success', text: 'Alert sent successfully!' });
        // Clear form after success
        setHeading('');
        setDescription('');
      } else {
        setStatusMessage({ type: 'error', text: response.error || 'Failed to send alert' });
      }
    };

    // Store handler ref for cleanup on unmount
    pendingHandlerRef.current = handleResponse;
    socket.once(SocketEvent.ADMIN_SEND_ALERT, handleResponse);
    socket.emit(SocketEvent.ADMIN_SEND_ALERT, request);
  }, [socket, selectedGame, icon, heading, description, duration]);

  // Clear status message after 3 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  if (!selectedGame) {
    return (
      <div className="w-100 align-self-stretch">
        <h2>📢 Notifications</h2>
        <p className="alert">Select a game from the dropdown above to send alerts.</p>
      </div>
    );
  }

  return (
    <div className="w-100 align-self-stretch notifications-panel">
      <h2>📢 Send Alert to All Players</h2>
      <p className="section-description">
        Send a custom alert notification to all players in <strong>{selectedGame.name || selectedGame.roomCode}</strong>
      </p>

      <div className="notifications-content">
        <div className="notifications-form">
          <div className="field">
            <label htmlFor="icon">Icon</label>
            <select
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="select"
            >
              {ICON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="heading">Heading</label>
            <input
              id="heading"
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              placeholder="ATTENTION PLAYERS"
              maxLength={50}
              className="input"
            />
            <span className="char-count">{heading.length}/50</span>
          </div>

          <div className="field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter your message here..."
              maxLength={200}
              rows={3}
              className="input"
            />
            <span className="char-count">{description.length}/200</span>
          </div>

          <div className="field">
            <label htmlFor="duration">
              Duration: <strong>{duration}s</strong>
            </label>
            <input
              id="duration"
              type="range"
              min="3"
              max="20"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="range"
            />
            <div className="duration-labels">
              <span>3s</span>
              <span>20s</span>
            </div>
          </div>

          {statusMessage && (
            <div className={`alert alert--sm ${statusMessage.type}`}>
              {statusMessage.text}
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={isSending || !heading.trim() || !description.trim()}
            className="btn"
          >
            {isSending ? 'Sending...' : '📤 Send Alert'}
          </button>
        </div>

        <div className="notifications-preview">
          <h3>Live Preview</h3>
          <div className="preview-container">
            <div className="notification-container">
              <div className="notification-item notification-item--admin-alert">
                <div className="notification__icon">{icon}</div>
                <div className="notification__heading">{heading || 'HEADING'}</div>
                <div className="notification__description">{description || 'Description text'}</div>
              </div>
            </div>
          </div>
          <p className="preview-note">
            This is how your alert will appear to players for {duration} seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
